import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PubNub from 'pubnub';
import { useDyteClient, DyteProvider } from '@dytesdk/react-web-core';
import { DyteMeeting } from '@dytesdk/react-ui-kit';
import styled, { keyframes } from 'styled-components';
import { loadDoctorState, changeDoctorStatus, doStartCall, doCompleteCall, applyOpsUpdate } from '../../actions/iciciQueue';
import config from '../../config';

const COLORS = {
  bg: '#0f1117',
  surface: '#1a1d26',
  border: '#2a2d3a',
  purple: '#8852cc',
  green: '#22c55e',
  yellow: '#f59e0b',
  red: '#ef4444',
  blue: '#3b82f6',
  textPrimary: '#f1f5f9',
  textMuted: '#94a3b8',
};

const STATUS_META = {
  AVAILABLE: { color: COLORS.green,     label: 'Available' },
  IN_CALL:   { color: COLORS.yellow,    label: 'In Call'   },
  WRAP_UP:   { color: COLORS.blue,      label: 'Wrap-Up'   },
  OFFLINE:   { color: COLORS.textMuted, label: 'Offline'   },
};

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeIn = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; }`;
const ripple = keyframes`
  0%   { box-shadow: 0 0 0 0 ${COLORS.green}44; }
  100% { box-shadow: 0 0 0 16px transparent; }
`;

// ─── Styled components ────────────────────────────────────────────────────────
const Page = styled.div`
  width: 100%;
  min-height: 100vh;
  background: ${COLORS.bg};
  color: ${COLORS.textPrimary};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 2rem;
`;

const TopNav = styled.div`
  width: 100%;
  max-width: 48rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1.2rem;
  border-bottom: 1px solid ${COLORS.border};
`;

const BackBtn = styled.button`
  background: transparent;
  border: 1px solid ${COLORS.border};
  color: ${COLORS.textMuted};
  border-radius: 0.5rem;
  padding: 0.4rem 1rem;
  font-size: 1.2rem;
  cursor: pointer;
  &:hover { border-color: ${COLORS.purple}; color: ${COLORS.purple}; }
`;

const Card = styled.div`
  background: ${COLORS.surface};
  border: 1px solid ${COLORS.border};
  border-radius: 1.2rem;
  padding: 2.4rem;
  width: 100%;
  max-width: 48rem;
  animation: ${fadeIn} 0.3s ease;
`;

const CardTitle = styled.h2`
  margin: 0 0 1.6rem;
  color: ${COLORS.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 1.1rem;
  font-weight: 600;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  margin-bottom: 2.4rem;
  flex-wrap: wrap;
`;

const StatusIndicator = styled.div`
  width: 1.2rem;
  height: 1.2rem;
  border-radius: 50%;
  background: ${(p) => p.color};
  animation: ${(p) => p.pulse ? ripple : 'none'} 1.8s infinite;
  flex-shrink: 0;
`;

const StatusLabel = styled.span`
  font-size: 2rem;
  font-weight: 700;
  color: ${(p) => p.color};
`;

const ToggleGroup = styled.div`
  display: flex;
  gap: 0.8rem;
  margin-left: auto;
`;

const ToggleBtn = styled.button`
  padding: 0.5rem 1.2rem;
  border-radius: 0.6rem;
  font-size: 1.2rem;
  font-weight: 600;
  border: 1.5px solid ${(p) => p.active ? p.activeColor : COLORS.border};
  background: ${(p) => p.active ? (p.activeColor + '22') : 'transparent'};
  color: ${(p) => p.active ? p.activeColor : COLORS.textMuted};
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: ${(p) => p.activeColor}; color: ${(p) => p.activeColor}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const CaseCard = styled.div`
  background: ${COLORS.bg};
  border: 1.5px solid ${COLORS.border};
  border-radius: 1rem;
  padding: 2rem;
  margin-bottom: 2rem;
  animation: ${fadeIn} 0.4s ease;
`;

const CaseTitle = styled.div`font-size: 2rem; font-weight: 700; margin-bottom: 0.4rem;`;

const CaseMeta = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.8rem 1.6rem;
  margin-top: 1.2rem;
`;

const MetaLabel = styled.div`font-size: 1.1rem; color: ${COLORS.textMuted};`;
const MetaValue = styled.div`font-size: 1.3rem; font-weight: 600;`;

const WaitBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${COLORS.yellow};
  background: ${COLORS.yellow}22;
  padding: 0.3rem 0.8rem;
  border-radius: 0.5rem;
  margin-top: 0.8rem;
`;

const StartBtn = styled.button`
  width: 100%;
  padding: 1.4rem;
  background: ${COLORS.green};
  color: #fff;
  border: none;
  border-radius: 0.8rem;
  font-size: 1.6rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover { opacity: 0.88; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const CompleteBtn = styled.button`
  width: 100%;
  padding: 1.1rem;
  background: transparent;
  color: ${COLORS.red};
  border: 1.5px solid ${COLORS.red};
  border-radius: 0.8rem;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.15s;
  &:hover { background: ${COLORS.red}22; }
`;

const IdleCard = styled(Card)`text-align: center; padding: 4rem 2rem;`;
const IdleIcon = styled.div`font-size: 4rem; margin-bottom: 1rem;`;
const IdleText = styled.div`font-size: 1.4rem; color: ${COLORS.textMuted};`;

const VideoWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  height: 70vh;
  border-radius: 1rem;
  overflow: hidden;
  border: 1px solid ${COLORS.border};
  margin-bottom: 1rem;
`;

const NewCaseToast = styled.div`
  position: fixed;
  top: 2rem; right: 2rem;
  background: ${COLORS.surface};
  border: 1.5px solid ${COLORS.purple};
  border-radius: 1rem;
  padding: 1.6rem 2rem;
  min-width: 28rem;
  box-shadow: 0 8px 24px rgba(0,0,0,0.4);
  animation: ${fadeIn} 0.3s ease;
  z-index: 9999;
`;

const ToastTitle = styled.div`font-size: 1.3rem; font-weight: 700; color: ${COLORS.purple}; margin-bottom: 0.8rem;`;
const ToastRow = styled.div`font-size: 1.2rem; color: ${COLORS.textMuted}; margin-bottom: 0.3rem; span { color: ${COLORS.textPrimary}; font-weight: 600; }`;

const fmtSeconds = (s) => {
  if (!s && s !== 0) return '—';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

// ─── Component ────────────────────────────────────────────────────────────────
function IciciDoctorPortal(props) {
  const { doctorId, doctorFirstName, doctorLastName, doctorStatus, assignedCase, history } = props;

  const [inActiveCall, setInActiveCall]   = useState(false);
  const [toast, setToast]                 = useState(null);
  const [starting, setStarting]           = useState(false);
  const pubnubRef                         = useRef(null);
  const toastTimerRef                     = useRef(null);

  // Dyte hook — must be called at top level of functional component
  const [meeting, initMeeting] = useDyteClient();

  useEffect(() => {
    props.loadDoctorState();
  }, []);

  // Re-subscribe whenever doctorId becomes available
  useEffect(() => {
    if (!doctorId) return;
    setupPubNub(doctorId);
    return () => {
      if (pubnubRef.current) pubnubRef.current.unsubscribeAll();
    };
  }, [doctorId]);

  const setupPubNub = (id) => {
    if (pubnubRef.current) pubnubRef.current.unsubscribeAll();
    const pn = new PubNub({
      publishKey:   config.pubnubPublishKey,
      subscribeKey: config.pubnubSubscribeKey,
      uuid: `doctor-portal-${id}`,
    });
    pn.addListener({
      message: ({ message }) => {
        if (message.name === 'new_case_inbound') {
          props.loadDoctorState();
          showToast({
            customerName:  message.customerName,
            applicationId: message.applicationId,
            waitSeconds:   message.waitSeconds,
          });
        }
        if (message.name === 'ops_queue_update') {
          props.applyOpsUpdate(message);
        }
      },
    });
    pn.subscribe({ channels: [`icici-instant-doctor-${id}`, 'icici-instant-ops'] });
    pubnubRef.current = pn;
  };

  const showToast = (data) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(data);
    toastTimerRef.current = setTimeout(() => setToast(null), 8000);
  };

  const handleStatusChange = (status) => {
    props.changeDoctorStatus(status);
  };

  const handleStartCall = async () => {
    if (!assignedCase || starting) return;
    setStarting(true);
    try {
      const result = await props.doStartCall(assignedCase.consultation_id);
      if (!result || result.status !== 'success') { setStarting(false); return; }
      await initMeeting({
        authToken: result.token,
        defaults: { audio: true, video: true },
      });
      setInActiveCall(true);
    } catch (err) {
      console.error('[handleStartCall]', err);
    }
    setStarting(false);
  };

  const handleCompleteCall = async () => {
    if (meeting) meeting.leaveRoom();
    await props.doCompleteCall(assignedCase.consultation_id);
    setInActiveCall(false);
  };

  const status     = doctorStatus ? doctorStatus.status : 'OFFLINE';
  const statusMeta = STATUS_META[status] || STATUS_META.OFFLINE;

  return (
    <Page>
      <TopNav>
        <BackBtn onClick={() => history && history.push('/home')}>← Back</BackBtn>
        <div style={{ fontSize: '1.3rem', fontWeight: 600, color: COLORS.textMuted }}>
          ICICI Instant vMER — Doctor Portal
        </div>
      </TopNav>

      {/* New-case toast */}
      {false && toast && (
        <NewCaseToast>
          <ToastTitle>New Case Assigned</ToastTitle>
          <ToastRow>Customer: <span>{toast.customerName}</span></ToastRow>
          <ToastRow>App ID: <span>{toast.applicationId}</span></ToastRow>
          {toast.waitSeconds > 0 && (
            <ToastRow>Waited: <span>{fmtSeconds(toast.waitSeconds)}</span></ToastRow>
          )}
        </NewCaseToast>
      )}

      {/* Doctor header */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <CardTitle>ICICI Instant vMER — Doctor Portal</CardTitle>
        <StatusRow>
          <StatusIndicator color={statusMeta.color} pulse={status === 'AVAILABLE'} />
          <StatusLabel color={statusMeta.color}>{statusMeta.label}</StatusLabel>
          <div style={{ color: COLORS.textMuted, fontSize: '1.3rem' }}>
            Dr. {doctorFirstName} {doctorLastName}
          </div>
          <ToggleGroup>
            <ToggleBtn
              active={status === 'AVAILABLE'}
              activeColor={COLORS.green}
              onClick={() => handleStatusChange('AVAILABLE')}
              disabled={status === 'IN_CALL'}
            >
              Go Available
            </ToggleBtn>
            <ToggleBtn
              active={status === 'OFFLINE'}
              activeColor={COLORS.textMuted}
              onClick={() => handleStatusChange('OFFLINE')}
              disabled={status === 'IN_CALL'}
            >
              Go Offline
            </ToggleBtn>
          </ToggleGroup>
        </StatusRow>
      </Card>

      {/* Active Dyte call */}
      {inActiveCall && meeting ? (
        <Card style={{ maxWidth: '100%', padding: '1rem' }}>
          <VideoWrapper>
            <DyteProvider value={meeting}>
              <DyteMeeting mode="fill" meeting={meeting} />
            </DyteProvider>
          </VideoWrapper>
          <CompleteBtn onClick={handleCompleteCall}>
            End Call &amp; Mark Complete
          </CompleteBtn>
        </Card>
      ) : assignedCase ? (
        /* Case card */
        <Card>
          <CardTitle>Next Case</CardTitle>
          <CaseCard>
            <CaseTitle>{assignedCase.name}</CaseTitle>
            {assignedCase.waitSeconds > 0 && (
              <WaitBadge>Waited {fmtSeconds(assignedCase.waitSeconds)}</WaitBadge>
            )}
            <CaseMeta>
              <div>
                <MetaLabel>Application ID</MetaLabel>
                <MetaValue>{assignedCase.applicationId}</MetaValue>
              </div>
              <div>
                <MetaLabel>Gender</MetaLabel>
                <MetaValue>{assignedCase.gender || '—'}</MetaValue>
              </div>
              <div>
                <MetaLabel>Phone</MetaLabel>
                <MetaValue>{assignedCase.phone}</MetaValue>
              </div>
              <div>
                <MetaLabel>Status</MetaLabel>
                <MetaValue>{assignedCase.status}</MetaValue>
              </div>
            </CaseMeta>
          </CaseCard>
          <StartBtn onClick={handleStartCall} disabled={starting}>
            {starting ? 'Connecting…' : 'Start Call Now'}
          </StartBtn>
        </Card>
      ) : (
        /* Idle */
        <IdleCard>
          <IdleIcon>🩺</IdleIcon>
          {status === 'OFFLINE' ? (
            <IdleText>You are offline. Click Go Available to start receiving cases.</IdleText>
          ) : status === 'WRAP_UP' ? (
            <IdleText>Wrap-up in progress… You'll be auto-set to Available shortly.</IdleText>
          ) : (
            <IdleText>You're available — waiting for the next case.</IdleText>
          )}
        </IdleCard>
      )}
    </Page>
  );
}

const mapStateToProps = (state) => ({
  doctorId:        state.user.doctorId,
  doctorFirstName: state.user.doctorFirstName,
  doctorLastName:  state.user.doctorLastName,
  doctorStatus:    state.iciciQueue.doctorStatus,
  assignedCase:    state.iciciQueue.assignedCase,
});

const mapDispatchToProps = {
  loadDoctorState,
  changeDoctorStatus,
  doStartCall,
  doCompleteCall,
  applyOpsUpdate,
};

export default connect(mapStateToProps, mapDispatchToProps)(IciciDoctorPortal);
